// Opens a real calendar invite for a single event. On native iOS we write the
// generated .ics to the Filesystem cache and hand it to the system share sheet,
// which offers the Calendar app as a target (the WKWebView <a download> trick
// used by downloadIcs does not reliably open Calendar.app on iOS). On web/dev we
// fall back to the same blob-download path so the button still works in a
// browser.

import { Capacitor } from "@capacitor/core";
import { buildIcs, downloadIcs, type IcsEvent } from "./ics";

export interface CalendarInviteEvent {
  uid: string;
  title: string;
  description?: string;
  location?: string;
  start: string; // YYYY-MM-DD or YYYY-MM-DDTHH:mm
  end?: string;
  url?: string;
}

function toIcsEvent(event: CalendarInviteEvent): IcsEvent {
  const description = event.url
    ? [event.description, event.url].filter(Boolean).join("\n")
    : event.description;
  return {
    uid: event.uid,
    title: event.title,
    description,
    location: event.location,
    start: event.start,
    end: event.end,
  };
}

async function shareNativeInvite(event: CalendarInviteEvent): Promise<void> {
  const [{ Filesystem, Directory, Encoding }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);

  const ics = buildIcs([toIcsEvent(event)]);
  const fileName = `event-${event.uid}.ics`;
  const written = await Filesystem.writeFile({
    path: fileName,
    data: ics,
    directory: Directory.Cache,
    encoding: Encoding.UTF8,
  });

  await Share.share({
    title: event.title,
    text: "Add to your calendar",
    files: [written.uri],
    dialogTitle: "Add to Calendar",
  });
}

// Generates the invite and opens it in the user's default calendar app. Throws
// on failure so callers can surface a toast.
export async function shareCalendarInvite(event: CalendarInviteEvent): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await shareNativeInvite(event);
    return;
  }
  downloadIcs(event.uid, [toIcsEvent(event)]);
}
